import styled from "@emotion/styled";
import { MenuProps } from "antd5";
import { Dropdown } from "antd5/lib";

import UserInitials, { UnassignedIcon } from "components/comments/UserInitials";
import Button from "components/ui/button/Button";
import { toast } from "components/ui/toast/Toast";
import { useCompany } from "lib/hooks/api/company/useCompany";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { useUpdateLeads } from "../../../lib/hooks/api/leads/useUpdateLeads";
import { pluralise } from "../../../lib/utils";

export function LeadAssigneeBulkUpdateButton({ selectedLeadIds }: { selectedLeadIds: string[] }) {
  const UNASSIGNED = "unassigned";

  const { data: company, isLoading: isLoadingCompany } = useCompany();

  const { mutate: updateLeads } = useUpdateLeads({
    onSuccess: (data) => {
      const assignee = data.updateLeads[0]?.assignedTo;
      const name = assignee ? `${assignee.firstName} ${assignee.lastName}` : "Unknown";

      toast.success(`${pluralise(data.updateLeads.length, "lead")} changed to '${name}'`);
    },
    onError: () => {
      toast.error("Failed to update lead assignee");
    },
  });

  const assigneeMenuItems: MenuProps["items"] =
    company?.users.map((user) => {
      const fullName = `${user.firstName} ${user.lastName}`;

      return {
        key: user.guid,
        title: fullName,
        label: fullName,
        icon: (
          <UserInitials
            firstName={user.firstName}
            lastName={user.lastName}
            showTooltip={false}
            type="extra-small"
          />
        ),
        onClick: () => {
          updateLeads({
            updateLeadsRequest: {
              ids: selectedLeadIds,
              updateData: { assignedToId: user.guid },
            },
          });
        },
      };
    }) || [];

  const unassignedOption = {
    label: "Unassigned",
    title: "",
    value: UNASSIGNED,
    icon: <UnassignedIcon showTooltip={false} type="extra-small" />,
    onClick: () => {
      updateLeads({
        updateLeadsRequest: { ids: selectedLeadIds, updateData: { assignedToId: null } },
      });
    },
    key: UNASSIGNED,
  };

  const allAssigneeMenuItems = [unassignedOption, ...assigneeMenuItems];

  return (
    <Dropdown
      menu={{ items: allAssigneeMenuItems }}
      trigger={["click"]}
      dropdownRender={(menu) => <DropdownContainer>{menu}</DropdownContainer>}
    >
      <Button icon="user01" loading={isLoadingCompany}>
        Assignee
        <UIcon icon="chevronDown" size={16} />
      </Button>
    </Dropdown>
  );
}

const DropdownContainer = styled.div({
  maxHeight: 300,
  overflow: "auto",
  borderRadius: 8,
  boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.12)",
});
